import React, { useEffect, useState } from "react";
import PatientService from "../../Services/PatientService";
import dayjs from "dayjs";
import { Modal, Button, Spin } from "antd";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";
import HRV from "../../assets/images/Svg/Hrv.svg";

const PulseOXChart = ({ smartWatchId }) => {
  const [tab, setTab] = useState("1D");
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [chartData1D, setChartData1D] = useState([]);
  const [chartData7D, setChartData7D] = useState([]);
  const [loading, setLoading] = useState(false);
  const [start7D, setStart7D] = useState(dayjs().subtract(6, "day"));
  const [end7D, setEnd7D] = useState(dayjs());
  const fetch1DData = async (date) => {
    if (!smartWatchId) return;

    const start = dayjs(date).format("DD/MM/YYYY");
    const payload = {
      type: "PULSEOX",
      userId: smartWatchId,
      fromDate: start,
      toDate: start,
      limit: 1,
      offset: 0,
    };

    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);

      if (res?.data?.length > 0) {
        const raw = JSON.parse(res.data[0].RAW_JSON || "{}");
        const samples = raw?.timeOffsetSpo2Values || {};

        const data = Object.entries(samples)
          .map(([offset, value]) => {
            const dateTime = dayjs(date)
              .startOf("day")
              .add(Number(offset), "minute");
            return {
              time: dateTime.toISOString(),
              value,
            };
          })
          .sort((a, b) => new Date(a.time) - new Date(b.time)); // sort by actual date/time

        setChartData1D(data);
      } else {
        setChartData1D([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetch7DData = async (startDate, endDate) => {
    if (!smartWatchId) return;
    const start = dayjs(startDate).format("DD/MM/YYYY");
    const end = dayjs(endDate).format("DD/MM/YYYY");
    const payload = {
      type: "PULSEOX",
      userId: smartWatchId,
      fromDate: start,
      toDate: end,
      limit: 100,
      offset: 0,
    };
    try {
      setLoading(true);
      const res = await PatientService.patientWatchData(payload);

      if (res?.data?.length > 0) {
        const data = res.data
          .sort((a, b) => a.CALENDAR_DATE - b.CALENDAR_DATE)
          .map((item) => ({
            date: dayjs(item.CALENDAR_DATE).local().format("DD/MM/YYYY"),
            value: Number(item.Value),
          }));

        setChartData7D(data);
      } else setChartData7D([]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "1D") {
      fetch1DData(selectedDate);
    } else {
      fetch7DData(start7D, end7D);
    }
  }, [tab, selectedDate, smartWatchId, start7D, end7D]);

  const handlePrevDay = () => setSelectedDate(selectedDate.subtract(1, "day"));
  const handleNextDay = () => setSelectedDate(selectedDate.add(1, "day"));

  const handlePrev7D = () => {
    const newStart = start7D.subtract(7, "day");
    const newEnd = end7D.subtract(7, "day");
    setStart7D(newStart);
    setEnd7D(newEnd);
  };

  const handleNext7D = () => {
    const today = dayjs();
    let newStart = start7D.add(7, "day");
    let newEnd = end7D.add(7, "day");

    if (newEnd.isAfter(today)) {
      newEnd = today;
      newStart = today.subtract(6, "day");
    }

    setStart7D(newStart);
    setEnd7D(newEnd);
  };

  return (
    <div>
      <div className="flex justify-center mb-4 gap-3">
        <Button
          type={tab === "1D" ? "primary" : "default"}
          onClick={() => setTab("1D")}
        >
          1D
        </Button>
        <Button
          type={tab === "7D" ? "primary" : "default"}
          onClick={() => setTab("7D")}
        >
          7D
        </Button>
      </div>

      {loading && <Loader />}

      {tab === "1D" && (
        <>
          <div className="flex items-center justify-center gap-4 mb-4">
            <Button onClick={handlePrevDay}>← Prev Day</Button>
            <span className="font-semibold">
              {selectedDate.format("DD/MM/YYYY")}
            </span>
            <Button onClick={handleNextDay}>Next Day →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData1D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="time"
                  ticks={
                    chartData1D.length > 0
                      ? [
                          chartData1D[0].time, // start
                          chartData1D[Math.floor(chartData1D.length / 2)].time, // middle
                          chartData1D[chartData1D.length - 1].time, // end
                        ]
                      : []
                  }
                  tickFormatter={(time) =>
                    dayjs(time).local().format("hh:mm A")
                  }
                />
                <YAxis />
                <Tooltip
                  labelFormatter={(label) =>
                    dayjs(label).local().format("hh:mm A")
                  }
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#ef4444"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {tab === "7D" && (
        <>
          <div className="flex justify-between items-center mb-4">
            <Button onClick={handlePrev7D}>← Prev 7 Days</Button>

            <span>
              {start7D.format("MMM D")} - {end7D.format("MMM D")}
            </span>

            <Button onClick={handleNext7D}>Next 7 Days →</Button>
          </div>
          <div className="mt-4 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData7D}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  dot
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {/* <div className="grid grid-cols-4 md:grid-cols-7 gap-3 mb-5">
            {chartData7D.map((item, idx) => {
              const dayName = item?.date
                ? dayjs(item.date, "DD/MM/YYYY").format("dddd")
                : "";
              const resting = item.restingHeartRateInBeatsPerMinute ?? 0;
              const max = item.maxHeartRateInBeatsPerMinute ?? 0;

              return (
                <div
                  key={idx}
                  className="bg-white shadow rounded-lg p-3 flex flex-col items-center gap-2"
                >
                  <p className="text-xs text-gray-500">{dayName}</p>

                  <div className="flex flex-col items-center justify-center gap-2 w-full">
                    <div className="flex items-center gap-2 w-full px-2">
                      <p className="text-xs text-gray-500">Resting Avg</p>
                      <p className="text-sm font-semibold text-red-500">
                        {resting} ms
                      </p>
                    </div>
                    <div className="flex items-center gap-2 w-full px-2">
                      <p className="text-xs text-gray-500">Max Avg</p>
                      <p className="text-sm font-semibold text-blue-500">
                        {max} ms
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div> */}
        </>
      )}
    </div>
  );
};

const Loader = () => (
  <div className="fixed inset-0 flex items-center justify-center bg-white/60 backdrop-blur-sm z-50">
    <div className="w-12 h-12 border-4 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
  </div>
);

export default PulseOXChart;
